import { AnalyticsEvent } from '../event'
import { GlobalDataFormatter } from './global-data'
import { MParticleProvider } from './mparticle/events/default'

/**
 * Visible Event Trigger. This will be triggered when an element is visible in the viewport.
 * @class
 * @extends AnalyticsEvent
 * @param {string} type - Event type
 * @param {string} value - Event value
 * @param {string} action - Event action
 * @param {Element} element - Element to observe
 */
export class Visible extends AnalyticsEvent {
    private observer: IntersectionObserver | undefined

    /**
     * Constructor
     * @param {string} type - Event type
     * @param {string} value - Event value
     * @param {string} action - Event action
     * @param {Element} element - Element to observe
     */
    public constructor(public type: string, public value: string, public action: string, public element?: Element) {
        super(type, value, action, element)
        this.observer = undefined
    }

    /**
     * Get the observer
     * @return {void}
     */
    public getObserver(): IntersectionObserver | undefined {
        return this.observer
    }

    /**
     * Handle event
     * @param {Event} event - Event object
     * @return {void}
     */
    public handle(event?: Event): void {
        /**
         * Event Handler: create Attributes list and send it to vendors providers
         * @var data will be different for each event, and for each provider
         *
         */
        // Get attributes list
        const dataAttrs = this.getGeneralAttributesList()

        // Get Data Values
        const globalFormatter = new GlobalDataFormatter(this.metaProperties)
        const data: any = {}
        dataAttrs.forEach((attr) => {
            try {
                const attrValue = globalFormatter.lookup(attr, event)
                if (attrValue !== '') {
                    data[attr] = attrValue
                }
            } catch (e) {
                console.error('GlobalDataFormatter error in class Visible', e)
            }
        })

        const controlFields = this.getControlFields()

        if (controlFields.mparticle) {
            const mparticle = new MParticleProvider(data, this.type, this.value, this.action, this.element)
            mparticle.setMetaProperties(this.metaProperties)
            mparticle.handle(event)
        }
    }

    /**
     * Observer Handler
     * @param {IntersectionObserverEntry} entry - IntersectionObserverEntry object
     * @return {void}
     */
    public observerHandler(entry: IntersectionObserverEntry): void {
        if (entry.intersectionRatio === 1.0 && this.observer) {
            this.handle(entry as unknown as Event)

            // Disconnect the observer after handling the event
            this.observer.unobserve(entry.target)
            this.removeEventListener()
        }
    }

    /**
     * Add event listener
     * @return {void}
     */
    public addEventListener(): void {
        if (!this.element) { return }

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.observerHandler(entry)
                        if (this.observer) {
                            this.observer.unobserve(entry.target)
                        }
                    }
                })
            },
            {
                threshold: 1.0,
            }
        )

        this.observer.observe(this.element as Element)
    }

    /**
     * Remove event listener
     * @return {void}
     */
    public removeEventListener(): void {
        if (this.observer) {
            this.observer.disconnect()
            this.observer = undefined // Set observer to undefined so you know it's been disconnected
        }
    }
}
